import { ArrowBack } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Grid2 as Grid,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useUser } from 'src/allauth/auth';
import PaymentAssignmentCard from 'src/components/PaymentAssignmentCard';
import ProgramProgressStepper from 'src/components/ProgramProgressStepper';
import ProgramProgressStepperCoach from 'src/components/ProgramProgressStepperCoach';
import ResponseGraphs from 'src/components/ResponseGraphs';
import { FadedPaper } from 'src/theme/StyledFields';
import { AssessmentDomainMap } from '../components/ResponseGraphs';
import ApiClient from '../config/ApiClient';
import { AthletePaymentAssignment } from '../object-actions/types/types';

const ProgressReport: React.FC = () => {
    const { athlete_id } = useParams<{ athlete_id: string }>();
    const location = useLocation();
    const user = useUser();
    const [assignment, setAssignment] = useState<AthletePaymentAssignment | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Check if assignment data was passed via navigation state
    const assignmentFromState = location.state?.assignment as AthletePaymentAssignment | undefined;

    const fetchAssignment = useCallback(async () => {
        if (!athlete_id) {
            setError('Athlete ID is required');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Fetch assignment for this specific athlete using the dedicated endpoint
            const response = await ApiClient.get<AthletePaymentAssignment>(
                `/api/athlete-assignments/${athlete_id}`
            );

            if (response.error) {
                setError(response.error);
            } else if (response.success && response.data) {
                setAssignment(response.data);
            } else {
                setError(`No assignment found for athlete ID: ${athlete_id}`);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch assignment data');
        } finally {
            setLoading(false);
        }
    }, [athlete_id]);

    useEffect(() => {
        // If assignment data was passed via navigation state, use it
        if (assignmentFromState && assignmentFromState.athlete_id === parseInt(athlete_id || '0', 10)) {
            setAssignment(assignmentFromState);
            setLoading(false);
        } else {
            // Otherwise, fetch from API
            fetchAssignment();
        }
    }, [athlete_id, assignmentFromState, fetchAssignment]);


    function getCategoryDisplayName(fieldName: string): string {
        return fieldName
            .replace('category_', '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    }

    function getSubmissionsWithScores() {
        if (!assignment?.submissions) return [];

        return assignment.submissions
            .filter(sub => sub.entity?.submitted_at) // Only show submitted assessments
            .sort((a, b) => {
                const dateA = a.entity?.submitted_at ? new Date(a.entity.submitted_at).getTime() : 0;
                const dateB = b.entity?.submitted_at ? new Date(b.entity.submitted_at).getTime() : 0;
                return dateA - dateB; // Sort by date ascending (oldest first)
            });
    }

    function calculateChange(current: number | null | undefined, previous: number | null | undefined): number | null {
        if (current === null || current === undefined || previous === null || previous === undefined) {
            return null;
        }
        return current - previous;
    }

    const categoryFields = Object.keys(AssessmentDomainMap);

    const submissionsWithScores = getSubmissionsWithScores();

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <LinearProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    if (!assignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No assignment found.
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Box p={2}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                    component={Link}
                    to="/dashboard"
                    color="secondary"
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" component="h1">
                    {assignment.athlete.id == user?.id ? 'My Progress' : 'Program Progress: ' + assignment.athlete.str}
                </Typography>
            </Box>

            <FadedPaper elevation={0} sx={{ mb: 4, p: 2 }}>
                <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <PaymentAssignmentCard assignment={assignment} />
                    </Grid>
                    {assignment.athlete.entity && assignment.athlete.entity?.category_leadership && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ResponseGraphs entity={assignment.athlete.entity} height={250} />
                        </Grid>
                    )}
                </Grid>
                <Typography variant="h5" component="h2" sx={{ mt: 3, mb: 1 }}>
                    Program Progress
                </Typography>
                {assignment.my_roles.includes('coach') ?
                    <ProgramProgressStepperCoach assignment={assignment} />
                    :
                    <ProgramProgressStepper assignment={assignment} />}
            </FadedPaper>

            {submissionsWithScores.length > 0 && (
                <FadedPaper elevation={0} sx={{ mb: 4, p: 2 }}>
                    <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                        Domain Scores by Assessment
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Domain</TableCell>
                                    {submissionsWithScores.map((submission, idx) => {
                                        const assessmentTitle = submission.entity?.assessment ?
                                            (typeof submission.entity.assessment === 'object' && 'str' in submission.entity.assessment
                                                ? submission.entity.assessment.str
                                                : `Assessment ${idx + 1}`)
                                            : `Assessment ${idx + 1}`;
                                        return (
                                            <TableCell key={submission.id} align="right">
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {assessmentTitle}
                                                    </Typography>
                                                    {submission.entity?.submitted_at && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(submission.entity.submitted_at).toLocaleDateString()}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                    {submissionsWithScores.length > 1 && (
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                Change
                                            </Typography>
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {categoryFields.map((fieldName) => {
                                    const displayName = getCategoryDisplayName(fieldName);
                                    const firstEntity = submissionsWithScores[0]?.entity;
                                    const lastEntity = submissionsWithScores[submissionsWithScores.length - 1]?.entity;
                                    const firstScore = firstEntity?.[fieldName as keyof typeof firstEntity] as number | null | undefined;
                                    const lastScore = lastEntity?.[fieldName as keyof typeof lastEntity] as number | null | undefined;
                                    const change = submissionsWithScores.length > 1 ? calculateChange(lastScore, firstScore) : null;

                                    return (
                                        <TableRow key={fieldName}>
                                            <TableCell component="th" scope="row">
                                                {displayName}
                                            </TableCell>
                                            {submissionsWithScores.map((submission) => {
                                                const entity = submission.entity;
                                                const score = entity?.[fieldName as keyof typeof entity] as number | null | undefined;
                                                return (
                                                    <TableCell key={submission.id} align="right">
                                                        {score !== null && score !== undefined ? score.toFixed(2) : '-'}
                                                    </TableCell>
                                                );
                                            })}
                                            {submissionsWithScores.length > 1 && (
                                                <TableCell align="right">
                                                    {change !== null ? (
                                                        <Chip
                                                            label={change > 0 ? `+${change.toFixed(2)}` : change.toFixed(2)}
                                                            size="small"
                                                            color={change > 0 ? 'success' : change < 0 ? 'error' : 'default'}
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </FadedPaper>
            )}
        </Box>
    );
};

export default ProgressReport;

